import { t } from 'i18next';
import { upperCase } from 'lodash';
import { useMemo } from 'react';
import { Control, UseFormReset } from 'react-hook-form';

import { ICompanySettings } from 'auth/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { FreightStatus } from 'quotes/enums';
import { getCostInputProps } from 'quotes/utils';
import { Size } from 'theme/enums';

import { FreightPriceType } from './enums';
import { FreightPriceChoice } from './FreightPriceChoice';
import { FreightValue } from './FreightValue';
import { IChangeVersionDataForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IChangeVersionDataForm>) => void;
  onSubmit: (reset: UseFormReset<IChangeVersionDataForm>) => (data: IChangeVersionDataForm) => void;
  partsTotal: number;
  freightSettings: Pick<ICompanySettings, 'defaultFreightPercentage' | 'minimumFreightCharge'>;
}

export const ChangeFreightForm = ({
  isOpen,
  onClose,
  onSubmit,
  partsTotal,
  freightSettings,
}: IProps) => {
  const { control, errors, getValues, setValue, watch } = useFormContext();
  const costDecimals = useCostDecimals();

  const freightStatusValue = watch('freightStatus');
  const freightPriceTypeValue = watch('freightPriceType');
  const freightCostValue = watch('freightCost');
  const freightPriceValue = watch('freightPrice');
  const freightPercentageValue = watch('freightPercentage');

  const priceWarning = useMemo(
    () =>
      NumberHelper.compare(freightPriceValue, freightSettings.minimumFreightCharge) < 0
        ? t('errorFreightTotalBellowMinimum', {
            minimum: PricingHelper.formatPricing({ amount: freightSettings.minimumFreightCharge }),
          })
        : '',
    [freightPriceValue, freightSettings.minimumFreightCharge]
  );

  const percentageWarning = useMemo(
    () =>
      NumberHelper.compare(
        Number(freightPercentageValue) / 100,
        freightSettings.defaultFreightPercentage
      ) < 0
        ? t('errorFreightPercentageBellowMinimum', {
            minimum: getPercentage(freightSettings.defaultFreightPercentage || 0),
          })
        : '',
    [freightPercentageValue, freightSettings.defaultFreightPercentage]
  );

  const onCostChange = (value: number) => {
    const margin = PricingHelper.computeAndFormatGpm(value, freightPriceValue);

    setValue('freightMargin', margin);
  };

  const onPriceChange = (value: number) => {
    const margin = PricingHelper.computeAndFormatGpm(freightCostValue, value);
    const percentage = (value * 100) / partsTotal;

    setValue('freightMargin', margin);
    setValue('freightPercentage', percentage);
  };

  const onMarginChange = (value: number) => {
    const price = freightCostValue
      ? PricingHelper.computeAndFormatPrice(freightCostValue, value / 100)
      : freightPriceValue;
    const percentage = (price * 100) / partsTotal;

    setValue('freightPrice', price);
    setValue('freightPercentage', percentage);
  };

  const onPercentageChange = (value: number) => {
    const price = (value / 100) * partsTotal;
    const margin = PricingHelper.computeAndFormatGpm(freightCostValue, price);

    setValue('freightPrice', price);
    setValue('freightMargin', margin);
  };

  return (
    <Form
      title={t('titleManageFreight')}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelChangeFreight')}
      onSubmit={onSubmit}
    >
      <FormField
        name="freightStatus"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelFreightType'),
          options: Object.values(FreightStatus).map((status) => ({
            label: upperCase(status),
            value: status,
          })),
        }}
      />

      {FreightStatus.OMITTED === freightStatusValue && (
        <FormField
          name="freightNote"
          type={InputType.TEXT_EDITOR}
          inputProps={{ label: t('labelFreightNote') }}
        />
      )}

      {FreightStatus.COMBINED === freightStatusValue && (
        <>
          <FormField
            name="freightCost"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              label: t('labelFreightCost'),
              onChange: ({ target: { value }, source }) => {
                if (source !== 'event') {
                  return;
                }

                onCostChange(Number(value));
              },
              ...getCostInputProps(costDecimals),
            }}
          />

          <FormField
            name="freightPriceType"
            type={InputType.RADIO_GROUP}
            inputProps={{
              size: Size.MD,
              labelSize: Size.MD,
              label: t('labelFreightPrice'),
              renderRadio: (choice) => (
                <FreightPriceChoice
                  key={choice.value}
                  control={control as unknown as Control<IChangeVersionDataForm>}
                  errors={errors}
                  choice={choice}
                  priceTypeValue={freightPriceTypeValue}
                  onPriceChange={onPriceChange}
                  onMarginChange={onMarginChange}
                  onPercentageChange={onPercentageChange}
                  priceWarning={priceWarning}
                  percentageWarning={percentageWarning}
                />
              ),
              choices: [
                { value: FreightPriceType.PRICE, label: t('labelFixedFreightPrice') },
                { value: FreightPriceType.PERCENTAGE, label: t('labelFreightPercentage') },
              ],
            }}
          />
        </>
      )}

      <FreightValue quoteVersionData={getValues()} label={t('labelTotalFreight')} />
    </Form>
  );
};
